// One editing surface owns input. Capture events before menus/cards receive
// them; map feature handlers also consult isMapEditing because their clicks
// originate on the allowed canvas. Nested preview/drawing sessions release
// independently, so closing an old session cannot unlock a newer one.
export interface EditLockOptions {
  surface: HTMLElement;
  controls: HTMLElement;
  extraControls?: () => Element[];
  cancel?: () => void;
  /** The session's own keys (markup's tool letters, nudges, undo), offered
   *  each key before stray ones are swallowed — never while typing. True
   *  consumes it. */
  onKey?: (event: KeyboardEvent) => boolean;
}
// A session is for an edit with STATE a stray click would throw away — a
// half-drawn ring, a dragged boundary, a preview form. A gesture that only
// waits for one map click (dropping or moving a point) takes no session: on
// a phone the panes are sheets over the map, and reaching the map means
// tapping one away first, which a lock can only refuse or cancel on.

const sessions: EditLockOptions[] = [];
let revision = 0;
const events = ['pointerdown', 'mousedown', 'touchstart', 'click', 'dblclick', 'contextmenu', 'wheel'] as const;
let notice: HTMLElement | null = null;
let noticeTimer: ReturnType<typeof setTimeout> | null = null;

/** A session whose controls have left the page, or been hidden, is over
 *  whether or not its owner said so: a legend form blanked by another flow,
 *  a bar removed by a replacement. Without this an unreleased lock is
 *  invisible and permanent — every click answers "finish your edit" with
 *  no edit on screen. Checked on every read, so a leak heals at once. */
function prune(): void {
  for (let i = sessions.length - 1; i >= 0; i--) {
    const c = sessions[i]!.controls as HTMLElement & { isConnected?: boolean; hidden?: boolean };
    if (c.isConnected === false || c.hidden === true) drop(sessions[i]!);
  }
}

function drop(session: EditLockOptions): void {
  const index = sessions.indexOf(session);
  if (index !== -1) sessions.splice(index, 1);
  if (sessions.length) return;
  for (const type of events) window.removeEventListener(type, block, true);
  window.removeEventListener('focusin', onFocus, true);
  window.removeEventListener('keydown', onKey, true);
  document.body.classList.remove('map-editing');
  clearNotice();
}

export function isMapEditing(): boolean { prune(); return sessions.length > 0; }
/** Invalidates navigation requests that started before an edit. */
export function mapEditRevision(): number { return revision; }

function allowed(target: EventTarget | null, session: EditLockOptions): boolean {
  return target instanceof Node && [session.surface, session.controls, ...(session.extraControls?.() ?? [])]
    .some(root => root.contains(target));
}

function explain(): void {
  if (!notice) {
    notice = document.createElement('div');
    notice.className = 'edit-session-notice';
    notice.setAttribute('role', 'status');
    notice.textContent = 'Finish or cancel your current edit before opening events or other menus.';
    document.body.appendChild(notice);
  }
  if (noticeTimer) clearTimeout(noticeTimer);
  noticeTimer = setTimeout(clearNotice, 3000);
}
function clearNotice(): void {
  if (noticeTimer) clearTimeout(noticeTimer);
  noticeTimer = null;
  notice?.remove(); notice = null;
}

function block(event: Event): void {
  prune();
  const session = sessions.at(-1);
  if (!session || allowed(event.target, session)) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (event.type === 'click' || event.type === 'pointerdown') explain();
}

function focusEditor(): void {
  const session = sessions.at(-1);
  if (!session) return;
  (session.controls.querySelector<HTMLElement>('button:not(:disabled), input, select, [tabindex="0"]')
    ?? session.surface).focus({ preventScroll: true });
}
function onFocus(event: FocusEvent): void {
  prune();
  const session = sessions.at(-1);
  if (session && !allowed(event.target, session)) {
    event.stopImmediatePropagation();
    focusEditor();
  }
}
function onKey(event: KeyboardEvent): void {
  prune();
  const session = sessions.at(-1);
  if (!session) return;
  const target = event.target as HTMLElement | null;
  const typing = allowed(target, session) && target?.closest?.('input, textarea, select, [contenteditable="true"]');
  if (event.key === 'Escape' && session.cancel && !typing) {
    event.preventDefault(); event.stopImmediatePropagation(); session.cancel(); return;
  }
  if (!typing && session.onKey?.(event)) {
    event.preventDefault(); event.stopImmediatePropagation(); return;
  }
  // Preserve typing, editor shortcuts and browser shortcuts. Unrelated app
  // keyboard handlers must not switch tools or navigate while editing.
  if (typing || event.ctrlKey || event.metaKey || ['Tab', 'Shift', 'Escape', 'Enter', ' ', 'Backspace', 'Delete', '[', ']'].includes(event.key)) return;
  event.stopImmediatePropagation();
}

export function beginMapEdit(session: EditLockOptions): () => void {
  revision++;
  if (!sessions.length) {
    for (const type of events) window.addEventListener(type, block, { capture: true, passive: false });
    window.addEventListener('focusin', onFocus, true);
    window.addEventListener('keydown', onKey, true);
    document.body.classList.add('map-editing');
  }
  sessions.push(session);
  // The menu button that started editing must not keep keyboard focus.
  if (!allowed(document.activeElement, session)) session.surface.focus({ preventScroll: true });
  let released = false;
  return () => {
    if (released) return;
    released = true;
    drop(session);
  };
}
